import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/components/ui/use-toast';
import {
  Operator,
  JobOperator,
  AssignOperatorRequest,
  RemoveOperatorRequest,
  OperatorSearchFilters,
  OperatorAssignmentSummary
} from '@/types/operators';

export const useOperatorOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search for available operators based on filters
  // Note: Since the operators table doesn't exist, we query the public.users table instead
  const searchOperators = useCallback(async (filters: OperatorSearchFilters = {}): Promise<Operator[]> => {
    setLoading(true);
    setError(null);
    try {
      // Query public.users table for workers
      let query = supabase
        .from('users')
        .select('id, email, full_name, status, department_id, created_at, updated_at')
        .eq('status', 'active'); // Only active users by default

      // Apply filters
      if (filters.department) {
        query = query.eq('department_id', filters.department);
      }
      if (filters.status) {
        const userStatus = filters.status === 'Active' ? 'active' : 
                          filters.status === 'Inactive' ? 'inactive' : 
                          filters.status === 'On Leave' ? 'inactive' : 'active';
        query = query.eq('status', userStatus);
      }

      const { data, error } = await query.order('full_name', { ascending: true });

      if (error) throw error;

      // Map users to operators
      const operators: Operator[] = (data || []).map(user => ({
        id: user.id,
        employee_id: user.id,
        first_name: user.full_name?.split(' ')[0] || '',
        last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
        email: user.email || '',
        department: user.department_id || '',
        role: 'Machine Operator',
        skill_level: 'Intermediate',
        certifications: [],
        status: user.status === 'active' ? 'Active' : 'Inactive',
        created_at: user.created_at || new Date().toISOString(),
        updated_at: user.updated_at || new Date().toISOString()
      }));

      // If available_only filter is set, check for current job assignments
      if (filters.available_only && operators.length > 0) {
        const operatorIds = operators.map(op => op.id);
        const { data: assignedJobCards } = await supabase
          .from('job_cards')
          .select('worker_id')
          .in('worker_id', operatorIds)
          .in('status', ['Not Started', 'In Progress', 'Paused']);

        const busyOperatorIds = new Set(assignedJobCards?.map(jc => jc.worker_id) || []);
        return operators.filter(op => !busyOperatorIds.has(op.id));
      }

      return operators;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to search operators: ${error.message}`,
        variant: 'destructive'
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get operators assigned to a specific job card
  // Note: Since job_operators and operators tables don't exist, we query job_cards and users tables
  const getJobOperators = useCallback(async (jobCardId: string): Promise<JobOperator[]> => {
    setLoading(true);
    setError(null);
    try {
      // Get job card information
      const { data: jobCard, error } = await supabase
        .from('job_cards')
        .select('*')
        .eq('id', jobCardId)
        .single();

      if (error) throw error;
      
      // If there's a worker assigned to this job card, fetch user data
      if (jobCard && jobCard.worker_id) {
        // Fetch user data from public.users table
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('id, email, full_name, department_id, created_at, updated_at')
          .eq('id', jobCard.worker_id)
          .single();
        
        if (userError) {
          console.error('Error fetching user data:', userError);
          throw userError;
        }
        
        if (user) {
          // Create operator object from user data
          const operator: Operator = {
            id: user.id,
            employee_id: user.id,
            first_name: user.full_name?.split(' ')[0] || '',
            last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
            email: user.email || '',
            department: user.department_id || '',
            role: 'Machine Operator',
            skill_level: 'Intermediate',
            certifications: [],
            status: 'Active', // Default to active
            created_at: user.created_at || new Date().toISOString(),
            updated_at: user.updated_at || new Date().toISOString()
          };

          // Create job operator object
          const jobOperator: JobOperator = {
            id: jobCard.id + '-' + user.id, // Create a unique ID
            job_card_id: jobCard.id,
            operator_id: user.id,
            assigned_at: jobCard.created_at,
            assigned_by: jobCard.assigned_by || jobCard.created_by || user.id,
            role_in_job: 'Primary Operator',
            is_primary: true,
            status: jobCard.status === 'Completed' ? 'Completed' : 'Active',
            created_at: jobCard.created_at,
            updated_at: jobCard.updated_at,
            // Relationships
            operator: operator
          };

          return [jobOperator];
        }
      }

      return [];
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to load job operators: ${error.message}`,
        variant: 'destructive'
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Assign an operator to a job card
  // Note: Since job_operators table doesn't exist, we update the job_cards table directly
  const assignOperator = useCallback(async (request: AssignOperatorRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Update job card with worker assignment
      const { error } = await supabase
        .from('job_cards')
        .update({
          worker_id: request.operator_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.job_card_id);

      if (error) throw error;

      // Create time log entry for this assignment
      const { error: timeLogError } = await supabase
        .from('time_entries')
        .insert({
          job_card_id: request.job_card_id,
          user_id: request.operator_id,
          start_time: new Date().toISOString(),
          status: 'Assigned',
          notes: `Assigned to job card ${request.job_card_id}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (timeLogError) throw timeLogError;

      toast({
        title: 'Success',
        description: 'Operator assigned successfully'
      });

      return true;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to assign operator: ${error.message}`,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove an operator from a job card
  // Note: Since job_operators table doesn't exist, we update the job_cards table directly
  const removeOperator = useCallback(async (request: RemoveOperatorRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // First, get the job card to get the worker_id
      const { data: jobCard, error: jobCardError } = await supabase
        .from('job_cards')
        .select('id, worker_id')
        .eq('id', request.job_operator_id) // Using job_operator_id as job_card_id
        .single();

      if (jobCardError) throw jobCardError;

      // Update job card to remove worker assignment
      const { error } = await supabase
        .from('job_cards')
        .update({
          worker_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.job_operator_id);

      if (error) throw error;

      // Close any active time logs for this operator
      const { error: timeLogError } = await supabase
        .from('time_entries')
        .update({
          end_time: new Date().toISOString(),
          status: 'Completed',
          updated_at: new Date().toISOString()
        })
        .eq('job_card_id', request.job_operator_id)
        .eq('user_id', jobCard.worker_id)
        .eq('status', 'In Progress');

      if (timeLogError) throw timeLogError;

      toast({
        title: 'Success',
        description: 'Operator removed successfully'
      });

      return true;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to remove operator: ${error.message}`,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Start operator work on a job card
  const startOperatorWork = useCallback(async (
    jobCardId: string,
    operatorId: string,
    notes?: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Update job card status
      const { error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'In Progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', jobCardId);

      if (jobCardError) throw jobCardError;

      // Create or update time log entry
      const { data: existingLogs } = await supabase
        .from('time_entries')
        .select('*')
        .eq('job_card_id', jobCardId)
        .eq('worker_id', operatorId)
        .is('end_time', null)
        .limit(1);

      if (existingLogs && existingLogs.length > 0) {
        // Update existing log
        const { error: timeLogError } = await supabase
          .from('time_entries')
          .update({
            start_time: new Date().toISOString(),
            status: 'In Progress',
            notes: notes || existingLogs[0].notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingLogs[0].id);

        if (timeLogError) throw timeLogError;
      } else {
        // Create new log
        const { error: timeLogError } = await supabase
          .from('time_entries')
          .insert({
            job_card_id: jobCardId,
            worker_id: operatorId,
            start_time: new Date().toISOString(),
            end_time: null,
            duration_minutes: 0,
            notes: notes || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (timeLogError) throw timeLogError;
      }

      return true;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to start operator work: ${error.message}`,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Pause operator work on a job card
  const pauseOperatorWork = useCallback(async (
    jobCardId: string,
    operatorId: string,
    notes?: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Update job card status
      const { error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'Paused',
          updated_at: new Date().toISOString()
        })
        .eq('id', jobCardId);

      if (jobCardError) throw jobCardError;

      // Update time log entry
      const { data: timeLogs } = await supabase
        .from('time_entries')
        .select('*')
        .eq('job_card_id', jobCardId)
        .eq('worker_id', operatorId)
        .is('end_time', null)
        .limit(1);

      if (!timeLogs || timeLogs.length === 0) {
        throw new Error('No active time log found for this operator');
      }

      const timeLog = timeLogs[0];

      // Calculate actual hours worked so far
      const startTime = new Date(timeLog.start_time);
      const currentTime = new Date();
      const hoursWorked = (currentTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      const durationMinutes = Math.round(hoursWorked * 60);

      // Update time log to paused
      const { error: timeLogError } = await supabase
        .from('time_entries')
        .update({
          end_time: currentTime.toISOString(),
          duration_minutes: durationMinutes,
          notes: notes || timeLog.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', timeLog.id);

      if (timeLogError) throw timeLogError;

      return true;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to pause operator work: ${error.message}`,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Complete operator work on a job card
  const completeOperatorWork = useCallback(async (
    jobCardId: string,
    operatorId: string,
    notes?: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Update job card status
      const { error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'Completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', jobCardId);

      if (jobCardError) throw jobCardError;

      // Update time log entry
      const { data: timeLogs } = await supabase
        .from('time_entries')
        .select('*')
        .eq('job_card_id', jobCardId)
        .eq('worker_id', operatorId)
        .is('end_time', null)
        .limit(1);

      if (!timeLogs || timeLogs.length === 0) {
        throw new Error('No active time log found for this operator');
      }

      const timeLog = timeLogs[0];

      // Calculate actual hours worked
      const startTime = new Date(timeLog.start_time);
      const endTime = new Date();
      const hoursWorked = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      const durationMinutes = Math.round(hoursWorked * 60);

      // Update time log to completed
      const { error: timeLogError } = await supabase
        .from('time_entries')
        .update({
          end_time: endTime.toISOString(),
          duration_minutes: durationMinutes,
          notes: notes || timeLog.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', timeLog.id);

      if (timeLogError) throw timeLogError;

      // Update job card with actual hours
      const { error: jobCardError2 } = await supabase
        .from('job_cards')
        .update({
          actual_hours: hoursWorked,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobCardId);

      if (jobCardError2) throw jobCardError2;

      toast({
        title: 'Success',
        description: 'Operator work completed successfully'
      });

      return true;
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to complete operator work: ${error.message}`,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get operator assignment summary for a job card
  // Note: Since job_operators and operators tables don't exist, we query job_cards and users tables
  const getOperatorAssignmentSummary = useCallback(async (jobCardId: string): Promise<OperatorAssignmentSummary> => {
    try {
      // Get job card information
      const { data: jobCard, error } = await supabase
        .from('job_cards')
        .select('*')
        .eq('id', jobCardId)
        .single();

      if (error) throw error;

      // Get active time logs for this job card
      const { data: timeLogs } = await supabase
        .from('time_entries')
        .select('*')
        .eq('job_card_id', jobCardId)
        .is('end_time', null);

      // If there's a worker assigned to this job card, fetch user data
      if (jobCard && jobCard.worker_id) {
        // Fetch user data from public.users table
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('id, email, full_name, status, department_id, created_at, updated_at')
          .eq('id', jobCard.worker_id)
          .single();
        
        if (userError) {
          console.error('Error fetching user data:', userError);
          throw userError;
        }
        
        if (user) {
          const operator: Operator = {
            id: user.id,
            employee_id: user.id,
            first_name: user.full_name?.split(' ')[0] || '',
            last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
            email: user.email || '',
            department: user.department_id || '',
            role: 'Machine Operator',
            skill_level: 'Intermediate',
            certifications: [],
            status: user.status === 'active' ? 'Active' : 'Inactive',
            created_at: user.created_at || new Date().toISOString(),
            updated_at: user.updated_at || new Date().toISOString()
          };

          const jobOperator: JobOperator = {
            id: jobCard.id + '-' + user.id, // Create a unique ID
            job_card_id: jobCard.id,
            operator_id: user.id,
            assigned_at: jobCard.created_at,
            assigned_by: jobCard.assigned_by || '',
            role_in_job: 'Primary Operator',
            is_primary: true,
            status: jobCard.status === 'Completed' ? 'Completed' : 'Active',
            created_at: jobCard.created_at,
            updated_at: jobCard.updated_at,
            // Relationships
            operator: operator
          };

          return {
            total_operators: 1,
            primary_operator: jobOperator,
            assistant_operators: [],
            active_time_logs: timeLogs?.length || 0,
            total_allocated_hours: 0,
            total_actual_hours: 0
          };
        }
      }

      return {
        total_operators: 0,
        primary_operator: undefined,
        assistant_operators: [],
        active_time_logs: timeLogs?.length || 0,
        total_allocated_hours: 0,
        total_actual_hours: 0
      };
    } catch (error: any) {
      setError(error.message);
      toast({
        title: 'Error',
        description: `Failed to load operator summary: ${error.message}`,
        variant: 'destructive'
      });
      return {
        total_operators: 0,
        primary_operator: undefined,
        assistant_operators: [],
        active_time_logs: 0,
        total_allocated_hours: 0,
        total_actual_hours: 0
      };
    }
  }, []);

  // Alias for getOperatorAssignmentSummary for backward compatibility
  const getOperatorSummary = getOperatorAssignmentSummary;

  return {
    loading,
    error,
    searchOperators,
    getJobOperators,
    assignOperator,
    removeOperator,
    startOperatorWork,
    pauseOperatorWork,
    completeOperatorWork,
    getOperatorSummary
  };
};
